"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import Head from "../Base/Head";
import Navbar from "./components/Navbar";
import ApiService from "../services/ApiService";

const MatchmakingHeroCompact = () => {
  return (
    <section className="portal-hero portal-hero--compact portal-hero--nonprofit-signup bg-9">
      <div className="portal-hero__overlay" />
      <div className="portal-hero__content">
        <h1 className="portal-hero__title">Non-profit Intake Form</h1>
        <p className="portal-hero__subtitle">
          Provide your organization&apos;s information to be included in the matchmaking portal for Apollo Global Management leaders.
        </p>
      </div>
    </section>
  );
};

const CheckboxRow = ({ id, label, checked, disabled, onChange }) => {
  return (
    <label
      htmlFor={id}
      className={`nonprofit-signup__choice ${disabled ? "is-disabled" : ""}`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="nonprofit-signup__choice-label">{label}</span>
    </label>
  );
};

const RadioRow = ({ id, name, label, checked, onChange }) => {
  return (
    <label htmlFor={id} className="nonprofit-signup__choice">
      <input id={id} type="radio" name={name} checked={checked} onChange={onChange} />
      <span className="nonprofit-signup__choice-label">{label}</span>
    </label>
  );
};

export default function NonProfitSignUp() {
  const formRef = useRef(null);
  const blurTimerRef = useRef(null);
  const pointerDownSectionRef = useRef(null);

  const user = useMemo(
    () => ({
      name: "Lauren Olivier",
      accountType: "EXECUTIVE",
      avatarUrl: "https://i.ibb.co/xqnrW3cK/woman-pfp.jpg",
      hasNotifications: true,
    }),
    []
  );

  const headquarterRegions = useMemo(
    () => ["North America", "Europe", "UK", "Africa", "Asia", "Latin America", "Global"],
    []
  );

  const usStates = useMemo(
    () => [
      { value: "AL", label: "Alabama" },
      { value: "AK", label: "Alaska" },
      { value: "AZ", label: "Arizona" },
      { value: "AR", label: "Arkansas" },
      { value: "CA", label: "California" },
      { value: "CO", label: "Colorado" },
      { value: "CT", label: "Connecticut" },
      { value: "DE", label: "Delaware" },
      { value: "DC", label: "District of Columbia" },
      { value: "FL", label: "Florida" },
      { value: "GA", label: "Georgia" },
      { value: "HI", label: "Hawaii" },
      { value: "ID", label: "Idaho" },
      { value: "IL", label: "Illinois" },
      { value: "IN", label: "Indiana" },
      { value: "IA", label: "Iowa" },
      { value: "KS", label: "Kansas" },
      { value: "KY", label: "Kentucky" },
      { value: "LA", label: "Louisiana" },
      { value: "ME", label: "Maine" },
      { value: "MD", label: "Maryland" },
      { value: "MA", label: "Massachusetts" },
      { value: "MI", label: "Michigan" },
      { value: "MN", label: "Minnesota" },
      { value: "MS", label: "Mississippi" },
      { value: "MO", label: "Missouri" },
      { value: "MT", label: "Montana" },
      { value: "NE", label: "Nebraska" },
      { value: "NV", label: "Nevada" },
      { value: "NH", label: "New Hampshire" },
      { value: "NJ", label: "New Jersey" },
      { value: "NM", label: "New Mexico" },
      { value: "NY", label: "New York" },
      { value: "NC", label: "North Carolina" },
      { value: "ND", label: "North Dakota" },
      { value: "OH", label: "Ohio" },
      { value: "OK", label: "Oklahoma" },
      { value: "OR", label: "Oregon" },
      { value: "PA", label: "Pennsylvania" },
      { value: "RI", label: "Rhode Island" },
      { value: "SC", label: "South Carolina" },
      { value: "SD", label: "South Dakota" },
      { value: "TN", label: "Tennessee" },
      { value: "TX", label: "Texas" },
      { value: "UT", label: "Utah" },
      { value: "VT", label: "Vermont" },
      { value: "VA", label: "Virginia" },
      { value: "WA", label: "Washington" },
      { value: "WV", label: "West Virginia" },
      { value: "WI", label: "Wisconsin" },
      { value: "WY", label: "Wyoming" },
    ],
    []
  );

  const citiesByState = useMemo(
    () => ({
      AL: ["Birmingham", "Montgomery", "Huntsville", "Mobile", "Other"],
      AK: ["Anchorage", "Fairbanks", "Juneau", "Other"],
      AZ: ["Phoenix", "Tucson", "Scottsdale", "Mesa", "Other"],
      AR: ["Little Rock", "Fayetteville", "Fort Smith", "Other"],
      CA: ["Los Angeles", "San Francisco", "San Diego", "San Jose", "Oakland", "Sacramento", "Palo Alto", "Other"],
      CO: ["Denver", "Boulder", "Colorado Springs", "Other"],
      CT: ["Greenwich", "Stamford", "Hartford", "New Haven", "Norwalk", "Westport", "Other"],
      DE: ["Wilmington", "Dover", "Newark", "Other"],
      DC: ["Washington", "Other"],
      FL: ["Miami", "Orlando", "Tampa", "Jacksonville", "Fort Lauderdale", "Other"],
      GA: ["Atlanta", "Savannah", "Augusta", "Other"],
      HI: ["Honolulu", "Other"],
      ID: ["Boise", "Other"],
      IL: ["Chicago", "Springfield", "Naperville", "Other"],
      IN: ["Indianapolis", "Fort Wayne", "Other"],
      IA: ["Des Moines", "Cedar Rapids", "Other"],
      KS: ["Kansas City", "Wichita", "Overland Park", "Other"],
      KY: ["Louisville", "Lexington", "Frankfort", "Other"],
      LA: ["New Orleans", "Baton Rouge", "Other"],
      ME: ["Portland", "Augusta", "Other"],
      MD: ["Baltimore", "Bethesda", "Rockville", "Annapolis", "Other"],
      MA: ["Boston", "Cambridge", "Worcester", "Springfield", "Other"],
      MI: ["Detroit", "Grand Rapids", "Ann Arbor", "Lansing", "Other"],
      MN: ["Minneapolis", "Saint Paul", "Rochester", "Other"],
      MS: ["Jackson", "Gulfport", "Other"],
      MO: ["Kansas City", "St. Louis", "Springfield", "Other"],
      MT: ["Billings", "Missoula", "Helena", "Other"],
      NE: ["Omaha", "Lincoln", "Other"],
      NV: ["Las Vegas", "Reno", "Henderson", "Other"],
      NH: ["Manchester", "Nashua", "Concord", "Other"],
      NJ: ["Newark", "Jersey City", "Princeton", "Trenton", "Hoboken", "Other"],
      NM: ["Albuquerque", "Santa Fe", "Other"],
      NY: ["New York City", "Buffalo", "Rochester", "Albany", "Syracuse", "Yonkers", "Other"],
      NC: ["Charlotte", "Raleigh", "Durham", "Greensboro", "Other"],
      ND: ["Fargo", "Bismarck", "Other"],
      OH: ["Columbus", "Cleveland", "Cincinnati", "Toledo", "Other"],
      OK: ["Oklahoma City", "Tulsa", "Other"],
      OR: ["Portland", "Eugene", "Salem", "Other"],
      PA: ["Philadelphia", "Pittsburgh", "Harrisburg", "Allentown", "Other"],
      RI: ["Providence", "Newport", "Warwick", "Other"],
      SC: ["Charleston", "Columbia", "Greenville", "Other"],
      SD: ["Sioux Falls", "Rapid City", "Other"],
      TN: ["Nashville", "Memphis", "Knoxville", "Chattanooga", "Other"],
      TX: ["Houston", "Dallas", "San Antonio", "Austin", "Fort Worth", "Other"],
      UT: ["Salt Lake City", "Provo", "Other"],
      VT: ["Burlington", "Montpelier", "Other"],
      VA: ["Richmond", "Virginia Beach", "Arlington", "Norfolk", "Other"],
      WA: ["Seattle", "Spokane", "Tacoma", "Bellevue", "Other"],
      WV: ["Charleston", "Huntington", "Other"],
      WI: ["Milwaukee", "Madison", "Green Bay", "Other"],
      WY: ["Cheyenne", "Jackson", "Other"],
    }),
    []
  );

  const professionalSkillsOptions = useMemo(
    () =>
      [
        "Finance / Accounting",
        "Investment Management",
        "Strategy",
        "Marketing / Communications",
        "Talent / Human Capital",
        "Legal / Compliance",
        "Risk management",
        "Operations",
        "Real Estate",
        "Technology / Engineering",
        "Data",
        "Fundraising",
      ].sort((a, b) => a.localeCompare(b)),
    []
  );

  const annualBudgetOptions = useMemo(
    () => [
      { value: "<$1m", label: "<$1 million" },
      { value: "$1-5m", label: "$1 - 5 million" },
      { value: "$5-10m", label: "$5 - 10 million" },
      { value: "$10-25m", label: "$10 - 25 million" },
      { value: ">$25m", label: ">$25 million" },
    ],
    []
  );

  const giveGetOptions = useMemo(
    () => [
      { value: "10", label: "Up to $10,000" },
      { value: "10-25", label: "$10,000 - $25,000" },
      { value: "25-50", label: "$25,000 - $50,000" },
      { value: "50-100", label: "$50,000 - $100,000" },
      { value: "no-disclose", label: "Prefer not to disclose" },
    ],
    []
  );

  const [orgName, setOrgName] = useState("");
  const [website, setWebsite] = useState("");
  const [linkedInPage, setLinkedInPage] = useState("");
  const [headquarterRegion, setHeadquarterRegion] = useState("");
  const [headquarterCountry, setHeadquarterCountry] = useState("");
  const [headquarterState, setHeadquarterState] = useState("");
  const [headquarterCity, setHeadquarterCity] = useState("");
  const [headquarterCityOther, setHeadquarterCityOther] = useState("");
  const [mission, setMission] = useState("");
  const [scaleSnapshot, setScaleSnapshot] = useState("");
  const [annualBudget, setAnnualBudget] = useState("");
  const [boardMeetingFrequency, setBoardMeetingFrequency] = useState("");
  const [boardMeetingFrequencyOther, setBoardMeetingFrequencyOther] = useState("");
  const [boardMeetingFormat, setBoardMeetingFormat] = useState("");
  const [financialContribution, setFinancialContribution] = useState("");
  const [giveGetExpanded, setGiveGetExpanded] = useState(false);
  const [openToFirstTimeBoard, setOpenToFirstTimeBoard] = useState("");
  const [professionalSkills, setProfessionalSkills] = useState([]);
  const [professionalSkillsOther, setProfessionalSkillsOther] = useState("");
  const [idealProfile, setIdealProfile] = useState("");
  const [apolloValue, setApolloValue] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactRoleTitle, setContactRoleTitle] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailLooksValid = (val) => {
    const v = `${val}`.trim();
    if (!v) return false;
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v);
  };

  const usaCityValid =
    headquarterCity.length > 0 &&
    (headquarterCity !== "Other" || headquarterCityOther.trim().length > 0);
  const headquarterValid =
    headquarterRegion.length > 0 &&
    (headquarterRegion !== "North America" ||
      (headquarterCountry.length > 0 &&
        (headquarterCountry !== "USA" ||
          (headquarterState.length > 0 && usaCityValid))));

  const formValid =
    orgName.trim().length > 0 &&
    website.trim().length > 0 &&
    headquarterValid &&
    mission.trim().length > 0 &&
    annualBudget.length > 0 &&
    scaleSnapshot.length > 0 &&
    professionalSkills.length > 0 &&
    idealProfile.trim().length > 0 &&
    (boardMeetingFrequency !== "other" || boardMeetingFrequencyOther.trim().length > 0) &&
    apolloValue.trim().length > 0 &&
    contactName.trim().length > 0 &&
    emailLooksValid(contactEmail);

  const head = {
    title: "Apollo Purpose Portal | Non-profit Application",
    description:
      "Provide your organization's information to be included in the matchmaking portal for Apollo Global Management leaders.",
    url: `https://apollopurposeportal.com/nonprofit-signup`,
    image: "",
  };

  const setProfessionalSkillChecked = (skill, checked) => {
    if (skill === "Other" && !checked) {
      setProfessionalSkillsOther("");
    }
    setProfessionalSkills((prev) => {
      if (checked) return Array.from(new Set([...prev, skill]));
      return prev.filter((s) => s !== skill);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!formValid) return;
    setSubmitted(false);

    const form = {
      orgName,
      website,
      linkedInPage,
      headquarterRegion,
      headquarterCountry,
      headquarterState,
      headquarterCity,
      headquarterCityOther,
      mission,
      scaleSnapshot,
      annualBudget,
      boardMeetingFrequency,
      boardMeetingFrequencyOther,
      boardMeetingFormat,
      financialContribution,
      openToFirstTimeBoard,
      professionalSkills,
      professionalSkillsOther,
      idealProfile,
      apolloValue,
      contactName,
      contactRoleTitle,
      contactEmail,
    };

    try {
      setIsSubmitting(true);
      const api = new ApiService();
      const response = await api.post(
        "landmark/apollo/submitNonProfitApplication",
        {
          form,
        }
      );
      if (!response || response.error || !response.success) {
        setSubmitted(false);
        toast.error(response?.error || "Could not submit form. Please try again.");
        return;
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitted(false);
      toast.error("Could not submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateActiveSectionClass = (activeSection) => {
    const formEl = formRef.current;
    if (!formEl) return;

    formEl
      .querySelectorAll(".nonprofit-signup__section.is-focus-active")
      .forEach((sectionEl) => {
        if (sectionEl !== activeSection) sectionEl.classList.remove("is-focus-active");
      });

    if (activeSection) activeSection.classList.add("is-focus-active");
  };

  const handleFormFocusCapture = (e) => {
    const sectionEl = e.target.closest?.(".nonprofit-signup__section");
    if (!sectionEl) return;

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    updateActiveSectionClass(sectionEl);
  };

  const handleFormMouseDownCapture = (e) => {
    const sectionEl = e.target.closest?.(".nonprofit-signup__section");
    if (!sectionEl) return;
    pointerDownSectionRef.current = sectionEl;
    updateActiveSectionClass(sectionEl);
  };

  const handleFormBlurCapture = (e) => {
    const sourceSection = e.target.closest?.(".nonprofit-signup__section");
    const nextSection = e.relatedTarget?.closest?.(".nonprofit-signup__section") || null;

    if (sourceSection && nextSection && sourceSection === nextSection) {
      updateActiveSectionClass(sourceSection);
      return;
    }

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
    }

    blurTimerRef.current = window.setTimeout(() => {
      const activeElement = document.activeElement;
      const activeSection = activeElement?.closest?.(".nonprofit-signup__section") || null;
      if (activeSection) {
        updateActiveSectionClass(activeSection);
      } else if (pointerDownSectionRef.current) {
        updateActiveSectionClass(pointerDownSectionRef.current);
      } else {
        updateActiveSectionClass(null);
      }
      pointerDownSectionRef.current = null;
      blurTimerRef.current = null;
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const pageStaggerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.08,
        delayChildren: 0.03,
      },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <>
      <Head head={head} noTwitterCard={true} />
      <div className="ed-wrapper">
        <div className="ed-main">
          <Navbar user={user} currentPage="portal" />

          <motion.div className="portal-page" initial="hidden" animate="show" variants={pageStaggerVariants}>
            <motion.div variants={fadeUpVariants}>
              <MatchmakingHeroCompact />
            </motion.div>

            {/* Keep header + form aligned to the same centered container width */}
            <motion.div className="nonprofit-signup__wrap" variants={fadeUpVariants}>
              <header className="nonprofit-signup__page-header">
                <h2 className="nonprofit-signup__heading">Non-profit Intake Form</h2>
                <p className="nonprofit-signup__intro">
                  Please complete the following form we can learn more about your organization and source the best-fit Apollo leaders for you to consider for your board.
                </p>
              </header>

              {submitted ? (
                <div className="nonprofit-signup__notice nonprofit-signup__notice--nonprofit" role="status">
                  <div className="nonprofit-signup__notice-title">Profile received</div>
                  <div className="nonprofit-signup__notice-text">
                    Thank you for submitting information to create your organization’s Apollo Purpose Portal profile. We appreciate your interest in sourcing an Apollo leader to be considered for your board, and will be in touch when there is a match. In the meantime, please email{" "}
                    <a
                      className="nonprofit-signup__notice-link"
                      href="mailto:Citizenship@apollo.com?subject=Apollo%20Purpose%20Portal%20Inquiry"
                    >
                      Citizenship@apollo.com
                    </a>{" "}
                    with any questions or feedback.
                  </div>
                </div>
              ) : null}

              <form
                ref={formRef}
                className="nonprofit-signup__card"
                onSubmit={handleSubmit}
                onFocusCapture={handleFormFocusCapture}
                onBlurCapture={handleFormBlurCapture}
                onMouseDownCapture={handleFormMouseDownCapture}
                onTouchStartCapture={handleFormMouseDownCapture}
              >
                {/* 1 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">1</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Organization overview</div>
                      <div className="nonprofit-signup__subkicker">
                        Basic details to identify your organization.
                      </div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__grid">
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Organization Name <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder="e.g., GreenGrid Foundation"
                      />
                      {submitAttempted && !orgName.trim().length ? (
                        <div className="nonprofit-signup__error">
                          Please enter your organization name.
                        </div>
                      ) : null}
                    </div>

                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Website <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="e.g., https://yourorg.org"
                      />
                      {submitAttempted && !website.trim().length ? (
                        <div className="nonprofit-signup__error">Please enter your website.</div>
                      ) : null}
                    </div>

                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">LinkedIn page</div>
                      <input
                        className="nonprofit-signup__input"
                        value={linkedInPage}
                        onChange={(e) => setLinkedInPage(e.target.value)}
                        placeholder="e.g., https://linkedin.com/company/your-org"
                      />
                    </div>
                  </div>
                </section>

                {/* 2 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">2</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Geography</div>
                      <div className="nonprofit-signup__subkicker">
                        Organization headquarter location (used for filtering and matching).
                      </div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__label">
                    Organization Headquarter Location <span className="nonprofit-signup__req">*</span>
                  </div>

                  <div className="nonprofit-signup__choices-grid">
                    {headquarterRegions.map((r) => {
                      const id = `hq-region-${r.replace(/\s+/g, "-").toLowerCase()}`;
                      return (
                        <RadioRow
                          key={r}
                          id={id}
                          name="headquarterRegion"
                          label={r}
                          checked={headquarterRegion === r}
                          onChange={() => setHeadquarterRegion(r)}
                        />
                      );
                    })}
                  </div>

                  {headquarterRegion === "North America" ? (
                    <div className="nonprofit-signup__grid" style={{ marginTop: "1rem" }}>
                      <div className="nonprofit-signup__field">
                        <div className="nonprofit-signup__label">
                          Country <span className="nonprofit-signup__req">*</span>
                        </div>
                        <select
                          className="nonprofit-signup__input"
                          value={headquarterCountry}
                          onChange={(e) => setHeadquarterCountry(e.target.value)}
                        >
                          <option value="">Select country</option>
                          <option value="USA">USA</option>
                          <option value="Canada">Canada</option>
                          <option value="Mexico">Mexico</option>
                          <option value="Other">Other</option>
                        </select>
                        {submitAttempted &&
                          headquarterRegion === "North America" &&
                          !headquarterCountry.length ? (
                          <div className="nonprofit-signup__error">Please select a country.</div>
                        ) : null}
                      </div>
                      {headquarterCountry === "USA" ? (
                        <>
                          <div className="nonprofit-signup__field">
                            <div className="nonprofit-signup__label">
                              State <span className="nonprofit-signup__req">*</span>
                            </div>
                            <select
                              className="nonprofit-signup__input"
                              value={headquarterState}
                              onChange={(e) => {
                                setHeadquarterState(e.target.value);
                                setHeadquarterCity("");
                              }}
                            >
                              <option value="">Select state</option>
                              {usStates.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            {submitAttempted &&
                              headquarterCountry === "USA" &&
                              !headquarterState.length ? (
                              <div className="nonprofit-signup__error">Please select a state.</div>
                            ) : null}
                          </div>
                          <div className="nonprofit-signup__field">
                            <div className="nonprofit-signup__label">
                              City <span className="nonprofit-signup__req">*</span>
                            </div>
                            <select
                              className="nonprofit-signup__input"
                              value={headquarterCity}
                              onChange={(e) => setHeadquarterCity(e.target.value)}
                              disabled={!headquarterState}
                            >
                              <option value="">
                                {headquarterState ? "Select city" : "Select state first"}
                              </option>
                              {(citiesByState[headquarterState] || []).map((city) => (
                                <option key={city} value={city}>
                                  {city}
                                </option>
                              ))}
                            </select>
                            {headquarterCity === "Other" ? (
                              <div className="nonprofit-signup__field" style={{ marginTop: "0.5rem" }}>
                                <input
                                  className="nonprofit-signup__input"
                                  value={headquarterCityOther}
                                  onChange={(e) => setHeadquarterCityOther(e.target.value)}
                                  placeholder="Specify city"
                                />
                                {submitAttempted &&
                                  headquarterCity === "Other" &&
                                  !headquarterCityOther.trim().length ? (
                                  <div className="nonprofit-signup__error">
                                    Please specify your city.
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {submitAttempted &&
                              headquarterCountry === "USA" &&
                              !headquarterCity.length ? (
                              <div className="nonprofit-signup__error">Please select a city.</div>
                            ) : null}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  {submitAttempted && !headquarterRegion.length ? (
                    <div className="nonprofit-signup__error">
                      Please select a headquarter location.
                    </div>
                  ) : null}
                </section>

                {/* 3 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">3</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Mission</div>
                      <div className="nonprofit-signup__subkicker">1–2 sentences.</div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__label">
                    What is your mission? <span className="nonprofit-signup__req">*</span>
                  </div>
                  <textarea
                    className="nonprofit-signup__textarea"
                    rows={3}
                    value={mission}
                    onChange={(e) => setMission(e.target.value)}
                    placeholder="What do you do, and for whom?"
                  />
                  {submitAttempted && !mission.trim().length ? (
                    <div className="nonprofit-signup__error">Please provide a short mission statement.</div>
                  ) : null}
                </section>

                {/* 4 intentionally omitted: issue areas section on hold */}

                {/* 4 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">4</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Scale snapshot</div>
                      <div className="nonprofit-signup__subkicker">
                        Budget, team, or impact scale (no numbers needed).
                      </div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__label">
                    Annual operating budget <span className="nonprofit-signup__req">*</span>
                  </div>

                  <div className="nonprofit-signup__choices-stack">
                    {annualBudgetOptions.map((opt) => (
                      <RadioRow
                        key={opt.value}
                        id={`budget-${opt.value.replace(/[^\w]+/g, "-")}`}
                        name="annualBudget"
                        label={opt.label}
                        checked={annualBudget === opt.value}
                        onChange={() => setAnnualBudget(opt.value)}
                      />
                    ))}
                  </div>

                  <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                    Which best describes your organization today?{" "}
                    <span className="nonprofit-signup__req">*</span>
                  </div>

                  <div className="nonprofit-signup__choices-stack">
                    <RadioRow
                      id="scale-early"
                      name="scaleSnapshot"
                      label="Early-stage / emerging"
                      checked={scaleSnapshot === "early"}
                      onChange={() => setScaleSnapshot("early")}
                    />
                    <RadioRow
                      id="scale-growing"
                      name="scaleSnapshot"
                      label="Growing / scaling"
                      checked={scaleSnapshot === "growing"}
                      onChange={() => setScaleSnapshot("growing")}
                    />
                    <RadioRow
                      id="scale-established"
                      name="scaleSnapshot"
                      label="Established / mature"
                      checked={scaleSnapshot === "established"}
                      onChange={() => setScaleSnapshot("established")}
                    />
                  </div>

                  {submitAttempted && (!annualBudget.length || !scaleSnapshot.length) ? (
                    <div className="nonprofit-signup__error">Please complete all scale snapshot fields.</div>
                  ) : null}
                </section>

                {/* 5 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">5</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Board need</div>
                      <div className="nonprofit-signup__subkicker">
                        Professional skills and board details.
                      </div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__label">
                    What professional skills are most valuable right now? (select all that apply){" "}
                    <span className="nonprofit-signup__req">*</span>
                  </div>

                  <div className="nonprofit-signup__choices-grid">
                    {professionalSkillsOptions.map((skill) => {
                      const id = `skill-${skill.replace(/[^\w]+/g, "-").toLowerCase()}`;
                      return (
                        <CheckboxRow
                          key={skill}
                          id={id}
                          label={skill}
                          checked={professionalSkills.includes(skill)}
                          disabled={false}
                          onChange={(checked) => setProfessionalSkillChecked(skill, checked)}
                        />
                      );
                    })}
                    <CheckboxRow
                      id="skill-other"
                      label="Other (specify)"
                      checked={professionalSkills.includes("Other")}
                      disabled={false}
                      onChange={(checked) => setProfessionalSkillChecked("Other", checked)}
                    />
                  </div>
                  {professionalSkills.includes("Other") ? (
                    <div className="nonprofit-signup__field" style={{ marginTop: "0.75rem" }}>
                      <input
                        className="nonprofit-signup__input"
                        value={professionalSkillsOther}
                        onChange={(e) => setProfessionalSkillsOther(e.target.value)}
                        placeholder="Please specify"
                      />
                    </div>
                  ) : null}

                  <div className="nonprofit-signup__label" style={{ marginTop: "1.25rem" }}>
                    How often does the board meet?
                  </div>
                  <div className="nonprofit-signup__choices-stack">
                    <RadioRow
                      id="meeting-monthly"
                      name="boardMeetingFrequency"
                      label="Monthly"
                      checked={boardMeetingFrequency === "monthly"}
                      onChange={() => setBoardMeetingFrequency("monthly")}
                    />
                    <RadioRow
                      id="meeting-quarterly"
                      name="boardMeetingFrequency"
                      label="Quarterly"
                      checked={boardMeetingFrequency === "quarterly"}
                      onChange={() => setBoardMeetingFrequency("quarterly")}
                    />
                    <RadioRow
                      id="meeting-other"
                      name="boardMeetingFrequency"
                      label="Other"
                      checked={boardMeetingFrequency === "other"}
                      onChange={() => setBoardMeetingFrequency("other")}
                    />
                  </div>
                  {boardMeetingFrequency === "other" ? (
                    <div className="nonprofit-signup__field" style={{ marginTop: "0.5rem" }}>
                      <input
                        className="nonprofit-signup__input"
                        value={boardMeetingFrequencyOther}
                        onChange={(e) => setBoardMeetingFrequencyOther(e.target.value)}
                        placeholder="Please specify frequency"
                      />
                    </div>
                  ) : null}

                  <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                    Are meetings in-person, virtual, or hybrid?
                  </div>
                  <div className="nonprofit-signup__choices-stack">
                    <RadioRow
                      id="format-inperson"
                      name="boardMeetingFormat"
                      label="In-person"
                      checked={boardMeetingFormat === "in-person"}
                      onChange={() => setBoardMeetingFormat("in-person")}
                    />
                    <RadioRow
                      id="format-virtual"
                      name="boardMeetingFormat"
                      label="Virtual"
                      checked={boardMeetingFormat === "virtual"}
                      onChange={() => setBoardMeetingFormat("virtual")}
                    />
                    <RadioRow
                      id="format-hybrid"
                      name="boardMeetingFormat"
                      label="Hybrid"
                      checked={boardMeetingFormat === "hybrid"}
                      onChange={() => setBoardMeetingFormat("hybrid")}
                    />
                  </div>

                  <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                    Is there a financial contribution expected (i.e., a give/get)?{" "}
                    <button
                      type="button"
                      className="nonprofit-signup__give-get-trigger"
                      onClick={() => setGiveGetExpanded((prev) => !prev)}
                      aria-expanded={giveGetExpanded}
                      aria-controls="nonprofit-give-get-definition"
                    >
                      What is give/get?
                    </button>
                  </div>
                  <AnimatePresence initial={false}>
                    {giveGetExpanded && (
                      <motion.div
                        id="nonprofit-give-get-definition"
                        className="nonprofit-signup__unlock-notice nonprofit-signup__give-get-notice"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                          opacity: { duration: 0.25 },
                        }}
                        style={{ overflow: "hidden", marginTop: "0.5rem" }}
                      >
                        <p className="nonprofit-signup__unlock-text nonprofit-signup__give-get-text">
                          A give/get policy is typically an annual, board-mandated requirement for directors to either personally donate a specific amount (&quot;give&quot;) or procure donations from their network (&quot;get&quot;). It ensures accountability, provides financial stability, and establishes clear fundraising expectations for leadership.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className="nonprofit-signup__choices-stack">
                    {giveGetOptions.map((opt) => (
                      <RadioRow
                        key={opt.value}
                        id={`contrib-${opt.value}`}
                        name="financialContribution"
                        label={opt.label}
                        checked={financialContribution === opt.value}
                        onChange={() => setFinancialContribution(opt.value)}
                      />
                    ))}
                  </div>

                  <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                    Are you open to first-time board members?
                  </div>
                  <div className="nonprofit-signup__choices-stack">
                    <RadioRow
                      id="firsttime-yes"
                      name="openToFirstTimeBoard"
                      label="Yes"
                      checked={openToFirstTimeBoard === "yes"}
                      onChange={() => setOpenToFirstTimeBoard("yes")}
                    />
                    <RadioRow
                      id="firsttime-no"
                      name="openToFirstTimeBoard"
                      label="No"
                      checked={openToFirstTimeBoard === "no"}
                      onChange={() => setOpenToFirstTimeBoard("no")}
                    />
                  </div>

                  {submitAttempted && professionalSkills.length === 0 ? (
                    <div className="nonprofit-signup__error">
                      Please select at least one professional skill area.
                    </div>
                  ) : null}
                  {submitAttempted &&
                  boardMeetingFrequency === "other" &&
                  !boardMeetingFrequencyOther.trim().length ? (
                    <div className="nonprofit-signup__error">
                      Please specify board meeting frequency.
                    </div>
                  ) : null}

                  <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                    Ideal profile for a board candidate <span className="nonprofit-signup__req">*</span>
                  </div>
                  <input
                    className="nonprofit-signup__input"
                    value={idealProfile}
                    onChange={(e) => setIdealProfile(e.target.value)}
                    placeholder="e.g., Leader with experience in climate tech, energy, or infrastructure."
                  />
                  {submitAttempted && !idealProfile.trim().length ? (
                    <div className="nonprofit-signup__error">
                      Please add a one-line ideal profile.
                    </div>
                  ) : null}
                </section>

                {/* 6 */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">6</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Apollo value-add</div>
                      <div className="nonprofit-signup__subkicker">1–2 sentences.</div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__label">
                    What are your board’s greatest needs right now? How would someone from Apollo best engage with and support your organization?{" "}
                    <span className="nonprofit-signup__req">*</span>
                  </div>
                  <textarea
                    className="nonprofit-signup__textarea"
                    rows={3}
                    value={apolloValue}
                    onChange={(e) => setApolloValue(e.target.value)}
                    placeholder="Be specific about the leverage you need."
                  />
                  {submitAttempted && !apolloValue.trim().length ? (
                    <div className="nonprofit-signup__error">Please provide a short answer.</div>
                  ) : null}
                </section>

                {/* 7 */}
                <section className="nonprofit-signup__section nonprofit-signup__section--last">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">7</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Primary Contact</div>
                      <div className="nonprofit-signup__subkicker">Who should we reach out to?</div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__grid">
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Primary Contact Name <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="Full name"
                      />
                      {submitAttempted && !contactName.trim().length ? (
                        <div className="nonprofit-signup__error">Please enter a contact name.</div>
                      ) : null}
                    </div>

                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Email <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="name@organization.org"
                      />
                      {submitAttempted && !emailLooksValid(contactEmail) ? (
                        <div className="nonprofit-signup__error">Please enter a valid email address.</div>
                      ) : null}
                    </div>
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">Role or Title</div>
                      <input
                        className="nonprofit-signup__input"
                        value={contactRoleTitle}
                        onChange={(e) => setContactRoleTitle(e.target.value)}
                        placeholder="e.g., Director"
                      />
                    </div>
                  </div>
                </section>

                <div className="nonprofit-signup__actions">
                  <button
                    type="submit"
                    className="nonprofit-signup__submit"
                    disabled={isSubmitting || (!formValid && submitAttempted)}
                  >
                    {isSubmitting ? "Submitting..." : "Submit application"}
                  </button>

                  {submitAttempted && !formValid ? (
                    <div className="nonprofit-signup__error nonprofit-signup__error--center">
                      Please complete the required fields above.
                    </div>
                  ) : (
                    <div className="nonprofit-signup__error-spacer" />
                  )}
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
